import { db } from '../firebase';
import { collection, serverTimestamp, writeBatch, doc, getDocs, query, where, orderBy, limit, deleteDoc, getDoc } from 'firebase/firestore';
import Papa from 'papaparse';
import { getStaff } from './staffService';
import { getSettings } from './settingsService';
import { getTenant } from './tenantService';
import { MasterTransaction, PayrollPeriod, ImportHistory } from '../types';
import { isPayrollPeriodLocked } from './payrollPeriodService';
import { matchesStaffLocation } from '../lib/transactionUtils';

export const getPendingImportSummary = async (tenantId: string) => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(
    masterRef, 
    where('type', '==', 'Submitted'),
    where('allocationStatus', '==', 'Pending'),
    orderBy('tipDate', 'desc'),
    limit(500)
  );
  
  const snapshot = await getDocs(q);
  const groups: Record<string, { date: string, count: number, total: number, ids: string[] }> = {};
  
  snapshot.docs.forEach(doc => {
    const data = doc.data() as MasterTransaction;
    const key = data.tipDate;
    if (!groups[key]) groups[key] = { date: key, count: 0, total: 0, ids: [] };
    groups[key].count++;
    groups[key].total += data.amount;
    groups[key].ids.push(doc.id);
  });
  
  return Object.values(groups);
};

export const getBatchTransactionSummary = async (tenantId: string, batchId: string) => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(masterRef, where('batchId', '==', batchId));
  const snapshot = await getDocs(q);
  
  const summary: Record<string, { staffId: string, staffName: string, totalAmount: number, count: number }> = {};
  
  snapshot.docs.forEach(doc => {
    const data = doc.data() as MasterTransaction;
    if (!summary[data.staffId]) {
      summary[data.staffId] = { staffId: data.staffId, staffName: data.staffName, totalAmount: 0, count: 0 };
    }
    summary[data.staffId].totalAmount += data.amount;
    summary[data.staffId].count++;
  });
  
  return Object.values(summary);
};

export const getImportHistory = async (tenantId: string): Promise<ImportHistory[]> => {
  const q = query(collection(db, 'tenants', tenantId, 'import_history'), orderBy('uploadedAt', 'desc'), limit(50));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ImportHistory));
};

export const deleteBatchTransactions = async (tenantId: string, ids: string[]) => {
  // Validate period lock status for all transactions in batch
  for (const id of ids) {
    const txDoc = await getDoc(doc(db, 'tenants', tenantId, 'master_transactions', id));
    if (txDoc.exists()) {
      const tx = txDoc.data() as MasterTransaction;
      if (await isPayrollPeriodLocked(tenantId, tx.tipDate)) {
        throw new Error('Cannot delete transactions from a locked or processed payroll period.');
      }
    }
  }

  const batch = writeBatch(db);
  ids.forEach(id => {
    const ref = doc(db, 'tenants', tenantId, 'master_transactions', id);
    batch.delete(ref);
  });
  await batch.commit();
};

const calculateHours = (start: string, end: string): number => {
  if (!start || !end) return 0;
  try {
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    
    let startMin = sH * 60 + sM;
    let endMin = eH * 60 + eM;
    
    // Handle overnight shifts
    if (endMin <= startMin) {
      endMin += 24 * 60;
    }
    
    return Number(((endMin - startMin) / 60).toFixed(2));
  } catch (e) {
    return 0;
  }
};

const normalizeDate = (d: string): string => {
  if (!d) return '';
  const clean = d.trim();
  // Handle MM/DD/YYYY or M/D/YYYY
  if (clean.includes('/')) {
    const parts = clean.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) {
        year = '20' + year;
      }
      return `${year}-${month}-${day}`;
    }
  }
  // Handle MM-DD-YYYY or DD-MM-YYYY or YYYY-MM-DD
  if (clean.includes('-')) {
    const parts = clean.split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
      } else if (parts[2].length === 4) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }
  }
  return clean;
};

export interface ImportResult {
  added: number;
  orphans: any[];
  failed: any[];
  total: number;
}

export const importPOSTips = async (
  tenantId: string, 
  file: File, 
  uploader: string, 
  fileHash: string, 
  isOverride: boolean = false,
  isCorrection: boolean = false,
  isPosTipAllocationRequired: boolean = false,
  targetBatchId?: string,
  locationId?: string,
  locationName?: string
): Promise<ImportResult> => {
  // Check for duplicate
  if (!isOverride && !isCorrection) {
    const importHistoryRef = collection(db, 'tenants', tenantId, 'import_history');
    const q = query(importHistoryRef, where('fileHash', '==', fileHash));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      throw new Error('Duplicate file');
    }
  }

  const [staffList, settings, periodsSnapshot, tenantDoc] = await Promise.all([
    getStaff(tenantId),
    getSettings(tenantId),
    getDocs(query(collection(db, 'tenants', tenantId, 'payroll_periods'), where('status', 'in', ['Locked', 'Processed']))),
    getTenant(tenantId)
  ]);

  const lockedPeriods = periodsSnapshot.docs.map(doc => doc.data() as PayrollPeriod);
  const mapping = settings?.posMapping || {};

  // Check state prohibition on merchant fee deduction (CA, MA, ME)
  const tenantState = (tenantDoc?.state || '').trim().toUpperCase();
  const isStateProhibitingFeeDeduction = tenantState === 'CA' || tenantState === 'MA' || tenantState === 'ME';
  const feeDeductionActive = !isStateProhibitingFeeDeduction && !!settings?.enableMerchantFeeDeduction && (settings?.merchantFeeRate || 0) > 0;
  const merchantFeeRate = feeDeductionActive ? (settings?.merchantFeeRate || 0) : 0;
  const enableServiceChargeDistribution = settings?.enableServiceChargeDistribution !== undefined ? settings.enableServiceChargeDistribution : true;
  const serviceChargeEmployeeShare = settings?.serviceChargeEmployeeShare !== undefined ? settings.serviceChargeEmployeeShare : 100;
  
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: ",",
      complete: async (results) => {
        console.log('DEBUG: PapaParse results meta.fields:', results.meta.fields);
        console.log('DEBUG: PapaParse results data:', results.data.slice(0, 2));
        if (results.data.length > 0) {
          console.log('DEBUG: First row keys:', Object.keys(results.data[0]));
          console.log('DEBUG: First row values:', Object.values(results.data[0]));
        }
        const batch = writeBatch(db);
        let added = 0;
        const orphans: any[] = [];
        const failed: any[] = [];
        const rows = results.data as any[];
        const batchId = `ba_${Date.now()}`;
        const effectiveLocationId = locationId && locationId !== 'all' ? locationId : 'loc-downtown';
        const effectiveLocationName = locationName || (effectiveLocationId === 'loc-downtown' ? 'Downtown Bistro' : 'Selected Store');
        
        // Handle Correction or Override Purge
        if ((isCorrection || isOverride) && targetBatchId) {
             const masterQ = query(collection(db, 'tenants', tenantId, 'master_transactions'), where('batchId', '==', targetBatchId));
             const tipsQ = query(collection(db, 'tenants', tenantId, 'pos_tips'), where('batchId', '==', targetBatchId));
             
             const [masterSnapshot, tipsSnapshot] = await Promise.all([getDocs(masterQ), getDocs(tipsQ)]);
             
             console.log(`DEBUG: Purge. Found ${masterSnapshot.size} master_transactions and ${tipsSnapshot.size} pos_tips for batch ${targetBatchId}`);
             
             if (!masterSnapshot.empty) {
                const txData = masterSnapshot.docs[0].data() as MasterTransaction;
                if (await isPayrollPeriodLocked(tenantId, txData.tipDate)) {
                  throw new Error('Cannot process batch within a locked or processed payroll period.');
                }
             }
             
             masterSnapshot.docs.forEach(d => {
                 console.log(`DEBUG: Deleting master_transaction ${d.id}, amount: ${d.data().amount}, batchId: ${d.data().batchId}`);
                 batch.delete(d.ref);
             });
             tipsSnapshot.docs.forEach(d => {
                 console.log(`DEBUG: Deleting pos_tip ${d.id}, amount: ${d.data().amount}, batchId: ${d.data().batchId}`);
                 batch.delete(d.ref);
             });
        }
        
        for (const row of rows) {
          // Dynamic Header Extraction with Mapping Bridge
          console.log(`DEBUG: Mapping object:`, JSON.stringify(mapping));
          
          const getField = (row: any, mappingKey: string, aliases: string[]) => {
            const mapped = mapping[mappingKey as keyof typeof mapping];
            let val = '';
            if (mapped && row.hasOwnProperty(mapped)) val = row[mapped];
            else {
              for (const alias of aliases) {
                if (row.hasOwnProperty(alias)) {
                  val = row[alias];
                  break;
                }
              }
            }
            return (typeof val === 'string' ? val.trim() : String(val).trim());
          };

          const rawTipDate = getField(row, 'tipDate', ['Tip Date', 'Date', 'Service Date', 'Business Date']);
          const tipDate = normalizeDate(rawTipDate);
          const rawAmount = getField(row, 'amount', ['Amount', 'Tip Amount', 'Total Tips', 'Tips', 'Tip']);
          const posId = getField(row, 'posId', ['POS_ID', 'POSID', 'POS ID', 'External ID', 'Staff ID', 'Employee ID']);
          const transactionId = getField(row, 'transactionId', ['Transaction ID', 'Trans ID', 'Order ID']);
          const transactionTimestamp = getField(row, 'transactionTimestamp', ['Timestamp', 'Time', 'Date Time', 'Transaction Time']);
          
          const rawServiceCharge = getField(row, 'serviceCharge', ['Service Charge', 'Auto Gratuity', 'Auto-Gratuity', 'Auto Grat', 'Gratuity', 'Service Fee', 'Mandatory Tip', 'Service_Charge', 'Auto_Gratuity', 'ServiceCharge']);
          const parsedServiceChargeAmount = (!isNaN(Number(rawServiceCharge)) && Number(rawServiceCharge) > 0) ? Math.round(Number(rawServiceCharge) * 100) / 100 : 0;
          const isRowExplicitServiceCharge = rawServiceCharge === 'true' || rawServiceCharge === '1' || getField(row, 'isServiceCharge', ['Is Service Charge', 'Service Charge Flag']) === 'true';

          const baseAmount = Math.round(Number(rawAmount) * 100) / 100;
          const shiftName = getField(row, 'shiftName', ['Shift Name', 'Shift', 'Service Period']);
          const shiftStartTime = getField(row, 'startTime', ['Shift Start', 'Start Time', 'Clock In', 'Check In']);
          const shiftEndTime = getField(row, 'endTime', ['Shift End', 'End Time', 'Clock Out', 'Check Out']);
          const hoursWorked = getField(row, 'hours', ['Hours', 'Hours Worked', 'Duration']) || calculateHours(shiftStartTime, shiftEndTime);

          // Validation
          const missingFields = [];
          if (!posId || posId === 'undefined') missingFields.push('POS ID');
          if (!shiftStartTime) missingFields.push('Shift Start Time');
          if (!shiftEndTime) missingFields.push('Shift End Time');
          if (!tipDate) missingFields.push('Tip Date');
          if ((isNaN(baseAmount) || baseAmount <= 0) && parsedServiceChargeAmount <= 0) missingFields.push('Amount');

          if (missingFields.length > 0) {
            console.warn(`DEBUG: Skipping row due to missing/invalid fields:`, {posId, shiftStartTime, shiftEndTime, tipDate, baseAmount, missingFields});
            if (rawAmount || posId || tipDate) {
                failed.push({
                  row,
                  reason: `Invalid or missing fields: ${missingFields.join(', ')}`,
                  data: { posId, shiftStartTime, shiftEndTime, tipDate, amount: baseAmount, rawAmount }
                });
            }
            continue;
          }

          if (lockedPeriods.some(p => tipDate >= p.startDate && tipDate <= p.endDate)) {
            failed.push({
              row,
              reason: 'Payroll period is locked',
              data: { tipDate }
            });
            continue;
          }

          // Compound POS Matching: Must match BOTH POS ID and Target Store
          const effectiveTargetLoc = locationId || 'loc-downtown';
          const staff = staffList.find(s => 
            String(s.posId) === String(posId) && 
            matchesStaffLocation(s, effectiveTargetLoc)
          );
          
          if (!staff) {
            orphans.push({
              posId,
              amount: baseAmount || parsedServiceChargeAmount,
              tipDate,
              shiftName,
              shiftStartTime,
              shiftEndTime,
              hoursWorked: Number(hoursWorked) || 0,
              targetLocationId: locationId || 'loc-downtown',
              targetLocationName: locationName || 'Target Store',
              originalRow: row
            });
            continue;
          }

          // Case A: Main Tip Line
          if (!isNaN(baseAmount) && baseAmount > 0) {
            let isServiceCharge = isRowExplicitServiceCharge;
            let grossAmount = baseAmount;
            let merchantFeeAmount = 0;
            let netAmount = baseAmount;

            if (isServiceCharge) {
              if (enableServiceChargeDistribution) {
                const distAmt = Math.round(baseAmount * (serviceChargeEmployeeShare / 100) * 100) / 100;
                grossAmount = distAmt;
                netAmount = distAmt;
              } else {
                grossAmount = 0;
                netAmount = 0;
              }
            } else {
              // Standard Credit Card Tip
              if (merchantFeeRate > 0) {
                merchantFeeAmount = Math.round(baseAmount * (merchantFeeRate / 100) * 100) / 100;
                netAmount = Math.round((grossAmount - merchantFeeAmount) * 100) / 100;
              }
            }

            if (netAmount > 0 || grossAmount > 0) {
              // 1. Write to pos_tips (Raw Intake)
              const tipRef = doc(collection(db, 'tenants', tenantId, 'pos_tips'));
              batch.set(tipRef, {
                tenantId,
                timestamp: serverTimestamp(),
                staffName: staff.name,
                email: staff.email,
                tipDate,
                amount: netAmount,
                grossAmount,
                merchantFeeAmount,
                shiftName,
                shiftStartTime,
                shiftEndTime,
                hoursWorked: Number(hoursWorked) || 0,
                status: 'Pending Audit',
                posId,
                staffId: staff.id!,
                source: isOverride ? 'POS Override' : 'POS',
                uploadedBy: uploader,
                isAuthoritativeOverride: isOverride,
                isServiceCharge,
                batchId,
                locationId: effectiveLocationId,
                locationName: effectiveLocationName
              });

              // 2. Write to master_transactions (Unified Ledger)
              const masterRef = doc(collection(db, 'tenants', tenantId, 'master_transactions'));
              const tx: MasterTransaction = {
                id: masterRef.id,
                tenantId,
                timestamp: serverTimestamp(),
                source: isOverride ? 'POS Override' : 'POS',
                type: 'Submitted',
                reportingStatus: 'pos_submitted',
                amount: netAmount,
                grossAmount,
                merchantFeeAmount,
                allocatedAmount: 0,
                tipDate,
                staffId: staff.id!,
                staffName: staff.name,
                email: staff.email,
                submittedBy: uploader,
                submissionStatus: 'Pending',
                allocationStatus: 'Pending',
                noAllocationRequired: false,
                payrollIncluded: false,
                analyticsIncluded: true,
                auditVerified: false,
                auditActor: isOverride ? 'Manager Override' : 'System Import',
                relatedTransactionId: tipRef.id,
                source_id: tipRef.id,
                auditContext: `Shift: ${shiftName} (${shiftStartTime}-${shiftEndTime})`,
                shiftName,
                shiftStartTime,
                shiftEndTime,
                hoursWorked: Number(hoursWorked) || 0,
                posId: posId,
                isAuthoritativeOverride: isOverride,
                isServiceCharge,
                batchId,
                locationId: effectiveLocationId,
                locationName: effectiveLocationName
              };
              if (transactionId) tx.transactionId = transactionId;
              if (transactionTimestamp) tx.transactionTimestamp = transactionTimestamp;
              batch.set(masterRef, tx);

              added++;
            }
          }

          // Case B: Separate Service Charge column present on the row
          if (!isRowExplicitServiceCharge && parsedServiceChargeAmount > 0 && enableServiceChargeDistribution) {
            const distSvc = Math.round(parsedServiceChargeAmount * (serviceChargeEmployeeShare / 100) * 100) / 100;
            if (distSvc > 0) {
              const svcTipRef = doc(collection(db, 'tenants', tenantId, 'pos_tips'));
              batch.set(svcTipRef, {
                tenantId,
                timestamp: serverTimestamp(),
                staffName: staff.name,
                email: staff.email,
                tipDate,
                amount: distSvc,
                grossAmount: distSvc,
                merchantFeeAmount: 0,
                shiftName,
                shiftStartTime,
                shiftEndTime,
                hoursWorked: Number(hoursWorked) || 0,
                status: 'Pending Audit',
                posId,
                staffId: staff.id!,
                source: isOverride ? 'POS Override' : 'POS',
                uploadedBy: uploader,
                isAuthoritativeOverride: isOverride,
                isServiceCharge: true,
                batchId,
                locationId: effectiveLocationId,
                locationName: effectiveLocationName
              });

              const svcMasterRef = doc(collection(db, 'tenants', tenantId, 'master_transactions'));
              const svcTx: MasterTransaction = {
                id: svcMasterRef.id,
                tenantId,
                timestamp: serverTimestamp(),
                source: isOverride ? 'POS Override' : 'POS',
                type: 'Submitted',
                reportingStatus: 'pos_submitted',
                amount: distSvc,
                grossAmount: distSvc,
                merchantFeeAmount: 0,
                allocatedAmount: 0,
                tipDate,
                staffId: staff.id!,
                staffName: staff.name,
                email: staff.email,
                submittedBy: uploader,
                submissionStatus: 'Pending',
                allocationStatus: 'Pending',
                noAllocationRequired: false,
                payrollIncluded: false,
                analyticsIncluded: true,
                auditVerified: false,
                auditActor: isOverride ? 'Manager Override' : 'System Import',
                relatedTransactionId: svcTipRef.id,
                source_id: svcTipRef.id,
                auditContext: `Service Charge / Auto-Grat - Shift: ${shiftName}`,
                shiftName,
                shiftStartTime,
                shiftEndTime,
                hoursWorked: Number(hoursWorked) || 0,
                posId: posId,
                isAuthoritativeOverride: isOverride,
                isServiceCharge: true,
                batchId,
                locationId: effectiveLocationId,
                locationName: effectiveLocationName
              };
              if (transactionId) svcTx.transactionId = `${transactionId}-SC`;
              batch.set(svcMasterRef, svcTx);

              added++;
            }
          }
        }

        // Only commit if we actually added something or if there are no orphans
        if (added > 0) {
          // Add import history
          const importRef = doc(collection(db, 'tenants', tenantId, 'import_history'));
          batch.set(importRef, {
            tenantId,
            fileHash,
            uploadedAt: serverTimestamp(),
            filename: file.name,
            addedCount: added,
            orphanCount: orphans.length,
            isCorrection,
            targetBatchId,
            batchId,
            locationId: effectiveLocationId,
            locationName: effectiveLocationName
          });
          await batch.commit();
        }

        resolve({ added, orphans, failed, total: rows.length });
      },
      error: (error: any) => reject(error)
    });
  });
};
